import i18n from '@/plugins/VueI18n';
import {
    PMChartBpcStatusEnum,
    PMChartStatusEnum,
    PMChartTypeEnum,
    PMDocumentPropertyEnum,
    PMProjectStatusEnum,
    PMStructureTypeEnum,
    PMTaskTypeEnum,
    WeeklyReportEnum,
} from '@/ship/Enums/ProjectManagementEnums';
import IFile from '@/ship/Models/IFile';
import { Structure } from '@/ship/Models/ProjectManagement';
import { ChartVersionStatusEnum } from '../Enums/ProjectManagement/ChartVersionEnum';
import { IIncludedValue } from '../Values/ITransformedValue';
import Project from './Project';
import User from './User';

export type TypeNode = IPMTableRow & {
    projects?: IPMProject[];
    chartTypes?: IPMChartType[];
};

export interface IPMTableRow {
    id: number;
    name?: string;
    isOpen?: boolean;
}

export interface IPMCity extends IPMTableRow {
    projects: IPMProject[];
}

export interface IPMProject extends IPMTableRow {
    code: string | null;
    status: PMProjectStatusEnum;
    fileUrl: string | null;
    chartTypes: IPMChartType[];
}

export interface IPMChartType extends IPMTableRow {
    charts: IPMChart[];
}

export interface IPMChart extends IPMTableRow {
    baseVersion?: IIncludedValue<IPMChartVersion>;
    calendarType: number;
    chartId?: string;
    chartName?: string;
    designerId: number;
    id: number;
    managerId: number;
    object?: PMDocumentPropertyEnum;
    project?: IIncludedValue<Project>;
    projectId: number;
    stage: string;
    startedAt: string;
    status: PMChartStatusEnum;
    type: PMChartTypeEnum;
    versions?: IIncludedValue<IPMChartVersion[]>;

    createdAt: string;
    updatedAt: string;
}

export interface IPMChartBpc extends IPMTableRow {
    id: number;
    name: string;
    isBase: boolean;
    acceptedDate: string;
    status: PMChartBpcStatusEnum;
    chartId: number;
    chart?: IIncludedValue<IPMChart>;
    authorId: number;
    mainEngineerId: number;
    branchDirectorId: number;
    regionalHeadId: number;
    regionalDesigningHeadId: number;
    constructionDirectorDeputyId: number;
    designingDirectorDeputyId: number;
    generalDirectorDeputyId: number;
    createdAt: string;
    updatedAt: string;
    tasks?: IIncludedValue<IPMChartTask[]>;
    businessAnalystId: number;
    designManagementCenterHeadId: number;
    generalDirectorId: number;
    projectManagerId: number;
}

export interface IPMChartDpc extends IPMTableRow {
    acceptedDate: string | null;
    authorId: number;
    branchDirectorId: number;
    chart?: IIncludedValue<IPMChart>;
    chartId: number;
    chartName: string;
    constructionDirectorDeputyId: number;
    createdAt: string;
    designingDirectorDeputyId: number;
    endDate: string;
    id: number;
    generalDirectorDeputyId: number;
    mainEngineerId: number;
    regionalDesigningHeadId: number;
    regionalHeadId: number;
    startDate: string;
    status: PMChartBpcStatusEnum;
    tasks?: IIncludedValue<IPMChartTask[]>;
    updatedAt: string;
    businessAnalystId: number;
    designManagementCenterHeadId: number;
    generalDirectorId: number;
    projectManagerId: number;
}

export interface IPMChartDpcExec extends IPMTableRow {
    acceptedDate: string | null;
    authorId: number;
    branchDirectorId: number;
    chart?: IIncludedValue<IPMChart>;
    chartId: number;
    chartName: string;
    constructionDirectorDeputyId: number;
    createdAt: string;
    designingDirectorDeputyId: number;
    detailedScheduleId: number;
    id: number;
    generalDirectorDeputyId: number;
    mainEngineerId: number;
    projectName: string;
    regionalDesigningHeadId: number;
    regionalHeadId: number;
    reviewerId?: number;
    selected: boolean;
    status: PMChartBpcStatusEnum;
    tasks?: IIncludedValue<IPMChartDpcExecTask[]>;
    updatedAt: string;
    businessAnalystId: number;
    designManagementCenterHeadId: number;
    generalDirectorId: number;
    projectManagerId: number;
}

export interface IPMChartWeeklyReport {
    acceptedDate: string | null;
    approvalStage: WeeklyReportEnum;
    authorId: number;
    baseScheduleBuffer: number | null;
    chartId: number;
    chartName: string;
    endDate: string | null;
    id: number;
    name: string | null;
    photo1: IIncludedValue<IFile>;
    photo2: IIncludedValue<IFile>;
    photo3: IIncludedValue<IFile>;
    photo4: IIncludedValue<IFile>;
    photoGeneralPlan: IIncludedValue<IFile>;
    photoGeneralPlanId: number | null;
    photoPlan1Id: number | null;
    photoPlan2Id: number | null;
    photoPlan3Id: number | null;
    photoPlan4Id: number | null;
    reviewerId: number | null;
    selected: boolean;
    startDate: string | null;
    tasks: IIncludedValue<IPMChartWeeklyReportTask[]>;
    versionBuffer: number | null;

    createdAt: string;
    updatedAt: string;
}

export interface IPMChartWeeklyReportTask {
    childTasks: IIncludedValue<IPMChartWeeklyReportTask[]>;
    code: string;
    completenessFact: number;
    completenessPlan: number;
    createdAt: string;
    description: string | null;
    id: number;
    isOpened?: boolean;
    isOverdue?: boolean;
    name: string;
    parentTaskId: number | null;
    photo: IIncludedValue<IFile[]>;
    photoCount: number;
    type: PMStructureTypeEnum;
    updatedAt: string;
    weeklyReportId: number;
}

export interface IPMChartRow extends IPMChart {
    selected: boolean;
}

export interface IPMChartDpcExecRow extends IPMChartDpcExec {
    selected: boolean;
}

export interface IPMChartWeeklyReportRow extends IPMChartDpcExec {
    selected: boolean;
}

export interface IPMChartDpcRow extends IPMChartDpc {
    selected: boolean;
}

export interface IPMChartBpcRow extends IPMChartBpc {
    chartName?: string;
    selected: boolean;
}

export interface IPMChartTask {
    id: number;
    isOverdue: boolean;
    name: string;
    type: number;
    code: string;
    completeness?: string;
    baseScheduleId?: number;
    detailedScheduleId?: number;
    parentTaskId: number | null;
    startDate: string;
    endDate: string;
    createdAt: string;
    updatedAt: string;
    childTasks?: IIncludedValue<IPMChartTask[]>;
}

export interface IPMChartDpcExecTask {
    childTasks?: IIncludedValue<IPMChartDpcExecTask[]>;
    code: string;
    completenessFact: number;
    completenessGeneral: number;
    completenessPlan: number;
    createdAt: string;
    description: string;
    detailedScheduleExecutionId: number;
    endDateFact: string;
    endDatePlan: string;
    id: number;
    isOverdue: boolean;
    name: string;
    parentTaskId: number | null;
    startDateFact: string;
    startDatePlan: string;
    type: number;
    updatedAt: string;
}

export interface IPMReportDeadlinesTask {
    chartVersionId: number;
    childTasks?: IIncludedValue<IPMReportDeadlinesTask[]>;
    code: string; // код СДР
    completenessFact: number; // Исполнение ДПГ
    completenessGeneral: number; // % гот
    completenessPredict: number; // % гот. (прогноз)
    createdAt: string;
    durationGeneral: number; // Длительность
    durationWorkingDays: number; // Длительность (рабочие дни)
    durationPredict: number; // Длительность (рабочие дни, прогноз)
    endDate: string; // Завершение
    endDateBaseSchedule: string; // Завершение по БП
    endDeviation: number; // Отклонение завершения
    executorId: number | null;
    id: number;
    isOverdue: boolean;
    name: string;
    parentTaskId: number | null;
    stage: string;
    startDate: string; // Начало
    startDateBaseSchedule: string; // Начало по БП
    startDeviation: number; // Отклонение начала
    type: PMTaskTypeEnum;
    updatedAt: string;
    weight: number;
}

export interface IPMReport {
    id: number;
    name: string;
    status: PMChartStatusEnum;
    bpcName: string;
    isBase: boolean;
    projectId: number;
    authorId: number;
    createdAt: string;
    object: PMDocumentPropertyEnum;
}

export interface IPMProjectFilter {
    planned: number;
    approved: number;
    stopped: number;
    completed: number;
}

export interface IPMChartFilters {
    managers: number[];
    projects: number[];
    projectStatuses: number[];
    designers: number[];
    chartTypes: PMChartTypeEnum[];
    statuses: PMChartStatusEnum[];
    startFrom: string;
    startTo: string;
}

export interface IPMDpcFilters {
    projects: number[];
    charts: number[];
    statuses: PMChartBpcStatusEnum[];
    authors: number[];
    startFrom: string;
    startTo: string;
    reportPeriodFrom: string;
    reportPeriodTo: string;
}

export interface IPMApprovalUsers {
    mainEngineerId: number;
    branchDirectorId: number;
    regionalHeadId: number;
    regionalDesigningHeadId: number;
    constructionDirectorDeputyId: number;
    designingDirectorDeputyId: number;
    generalDirectorDeputyId: number;
    designManagementCenterHeadId: number;
    generalDirectorId: number;
    projectManagerId: number;
    businessAnalystId: number;
}

export const PMChartStatuses = new Map([
    [
        PMChartStatusEnum.planned,
        {
            code: PMChartStatusEnum[PMChartStatusEnum.planned],
            label: i18n.t('page.projectManagement.status.planned'),
            color: '#8729D1', // purple (changed)
            background: '#F7F5FF',
            border: '#F7F5FF',
        },
    ],
    [
        PMChartStatusEnum.approved,
        {
            code: PMChartStatusEnum[PMChartStatusEnum.approved],
            label: i18n.t('page.projectManagement.status.approved'),
            color: '#30C07C', // green
            background: '#F3FCF8',
            border: '#C6EED1',
        },
    ],
    [
        PMChartStatusEnum.stopped,
        {
            code: PMChartStatusEnum[PMChartStatusEnum.stopped],
            label: i18n.t('page.projectManagement.status.stopped'),
            color: '#DA8E1B', // orange / red
            background: '#FCF8EF',
            border: '#FCF8EF',
        },
    ],
    [
        PMChartStatusEnum.completed,
        {
            code: PMChartStatusEnum[PMChartStatusEnum.completed],
            label: i18n.t('page.projectManagement.status.completed'),
            color: '#27B2B2', // sky blue
            background: '#F3FCFC',
            border: '#F3FCFC',
        },
    ],
    [
        PMChartStatusEnum.cancelled,
        {
            code: PMChartStatusEnum[PMChartStatusEnum.cancelled],
            label: i18n.t('page.projectManagement.status.cancelled'),
            color: '#EB4A14', // per
            background: '#FCEFEF',
            border: '#FCEFEF',
        },
    ],
]);

const StatusesExtended = new Map(PMChartStatuses);

export const PMChartStatusesExtended = StatusesExtended.set(PMChartStatusEnum.cancelled, {
    code: PMChartStatusEnum[PMChartStatusEnum.cancelled],
    label: i18n.t('page.projectManagement.status.cancelled'),
    color: '#EB4A14', // red
    background: '#FCEFEF',
    border: '#FCEFEF',
});

export const PMProjectStatuses = new Map([
    [
        PMProjectStatusEnum.decidingLaunch,
        {
            code: PMProjectStatusEnum[PMProjectStatusEnum.decidingLaunch],
            label: i18n.t('page.projectManagement.status.decidingLaunch'),
            color: '#8729D1', // purple (changed)
            background: '#F7F5FF',
            border: '#F7F5FF',
        },
    ],
    [
        PMProjectStatusEnum.designStarted,
        {
            code: PMProjectStatusEnum[PMProjectStatusEnum.designStarted],
            label: i18n.t('page.projectManagement.status.designStarted'),
            color: '#30C07C', //green
            background: '#E9F8ED',
            border: '#C6EED1',
        },
    ],
    [
        PMProjectStatusEnum.constructionUnderway,
        {
            code: PMProjectStatusEnum[PMProjectStatusEnum.constructionUnderway],
            label: i18n.t('page.projectManagement.status.constructionUnderway'),
            color: '#EB4A14', // orange / red
            background: '#FCEFEF',
            border: '#FCEFEF',
        },
    ],
    [
        PMProjectStatusEnum.workStopped,
        {
            code: PMProjectStatusEnum[PMProjectStatusEnum.workStopped],
            label: i18n.t('page.projectManagement.status.stopped'),
            color: '#EB4A14', // orange / red
            background: '#FCEFEF',
            border: '#FCEFEF',
        },
    ],
    [
        PMProjectStatusEnum.completed,
        {
            code: PMProjectStatusEnum[PMProjectStatusEnum.completed],
            label: i18n.t('page.projectManagement.status.completed'),
            color: '#27B2B2', // sky blue
            background: '#F3FCFC',
            border: '#F3FCFC',
        },
    ],
]);

export const ChartVersionStatuses = new Map([
    [
        ChartVersionStatusEnum.current,
        {
            code: ChartVersionStatusEnum[ChartVersionStatusEnum.current],
            label: 'Текущий',
            color: '#DA8E1B',
            background: '#FCF8EF',
            border: '#E5E4E5',
        },
    ],
    [
        ChartVersionStatusEnum.approved,
        {
            code: ChartVersionStatusEnum[ChartVersionStatusEnum.approved],
            label: 'Утвержден',
            color: '#30C07C',
            background: '#F3FCF8',
            border: '#F3FCF8',
        },
    ],
    [
        ChartVersionStatusEnum.archive,
        {
            code: ChartVersionStatusEnum[ChartVersionStatusEnum.archive],
            label: 'Архивный',
            color: '#8729D1',
            background: '#F7F5FF',
            border: '#F7F5FF',
        },
    ],
    [
        ChartVersionStatusEnum.onApproval,
        {
            code: ChartVersionStatusEnum[ChartVersionStatusEnum.onApproval],
            label: 'На согласовании',
            color: '#DA8E1B',
            background: '#FCF8EF',
            border: '#FCF8EF',
        },
    ],
    [
        ChartVersionStatusEnum.cancelled,
        {
            code: ChartVersionStatusEnum[ChartVersionStatusEnum.cancelled],
            label: 'Отклонен',
            color: '#EB4A14',
            background: '#FCEFEF',
            border: '#FCEFEF',
        },
    ],
]);

export const PMChartBpcStatuses = new Map([
    [
        PMChartBpcStatusEnum.prepared,
        {
            code: PMChartBpcStatusEnum[PMChartBpcStatusEnum.prepared],
            label: i18n.t('page.projectManagement.status.prepared'),
            color: '#8729D1', // purple (changed)
            background: '#F7F5FF',
            border: '#F7F5FF',
        },
    ],
    [
        PMChartBpcStatusEnum.agreed,
        {
            code: PMChartBpcStatusEnum[PMChartBpcStatusEnum.agreed],
            label: i18n.t('page.projectManagement.status.agreed'),
            color: '#30C07C', // green
            background: '#E9F8ED',
            border: '#C6EED1',
        },
    ],
    [
        PMChartBpcStatusEnum.notAgreed,
        {
            code: PMChartBpcStatusEnum[PMChartBpcStatusEnum.notAgreed],
            label: i18n.t('page.projectManagement.status.notAgreed'),
            color: '#EB4A14', // orange / red
            background: '#FCEFEF',
            border: '#FCEFEF',
        },
    ],
    [
        PMChartBpcStatusEnum.onApproval,
        {
            code: PMChartBpcStatusEnum[PMChartBpcStatusEnum.onApproval],
            label: i18n.t('page.projectManagement.status.onApproval'),
            color: '#DA8E1B', // light orange
            background: '#FCF8EF',
            border: '#FCF8EF',
        },
    ],
]);

export const WeeklyReportStatuses = new Map([
    [
        WeeklyReportEnum.CompiledBySupervisor,
        {
            code: WeeklyReportEnum[WeeklyReportEnum.CompiledBySupervisor],
            label: i18n.t('page.projectManagement.status.prepared'),
            color: '#8729D1', // purple (changed)
            background: '#F7F5FF',
            border: '#F7F5FF',
        },
    ],
    [
        WeeklyReportEnum.OnApprovalByPM,
        {
            code: WeeklyReportEnum[WeeklyReportEnum.OnApprovalByPM],
            label: i18n.t('page.projectManagement.status.onApproval'),
            color: '#DA8E1B', // light orange
            background: '#FCF8EF',
            border: '#FCF8EF',
        },
    ],
    [
        WeeklyReportEnum.OnApprovalByCS,
        {
            code: WeeklyReportEnum[WeeklyReportEnum.OnApprovalByCS],
            label: i18n.t('page.projectManagement.status.onApproval'),
            color: '#DA8E1B', // light orange
            background: '#FCF8EF',
            border: '#FCF8EF',
        },
    ],
    [
        WeeklyReportEnum.Agreed,
        {
            code: WeeklyReportEnum[WeeklyReportEnum.Agreed],
            label: i18n.t('page.projectManagement.status.agreed'),
            color: '#30C07C', // green
            background: '#E9F8ED',
            border: '#C6EED1',
        },
    ],
]);

export interface IPMChartVersion {
    approvalStatus: ChartVersionStatusEnum;
    authorId: number;
    baseSchedules: IIncludedValue<IPMChartBpc[]>;
    chartId: number;
    createdAt: string;
    description?: string;
    id: number;
    isActualVersion: boolean;
    isBackup: boolean;
    isBaseVersion: boolean;
    hasTasks: boolean;
    name: string;
    status: number;
    stage?: string;
    tasks?: IIncludedValue<Structure[]>;
    startedAt: string;
    updatedAt: string;
}

export interface IPMChartVersionRow extends IPMChartVersion {
    selected: boolean;
}

export interface IPMChartHistory {
    id: number;
    authorId: number;
    updatedAt: string;
    createdAt: string;
    data: {
        status: {
            from: PMChartStatusEnum;
            to: PMChartStatusEnum;
        };
    };

    author?: IIncludedValue<User>;
    message?: string;
    submessage?: string;
}
